import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, StickyNote, ListChecks, Trash2, ShieldAlert, Wrench, Ban, UserRound, CalendarDays } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FormSheet } from "./CrudPage";

const ACTIONS = [
  { key: "note", label: "Manager Note", icon: StickyNote, module: "feed" },
  { key: "task", label: "Task", icon: ListChecks, module: "tasks" },
  { key: "waste", label: "Log Waste", icon: Trash2, module: "waste" },
  { key: "incident", label: "Incident", icon: ShieldAlert, module: "incidents" },
  { key: "maintenance", label: "Maintenance Issue", icon: Wrench, module: "log" },
  { key: "item86", label: "86'd Item", icon: Ban, module: "log" },
  { key: "guest", label: "Guest Issue", icon: UserRound, module: "log" },
  { key: "beo", label: "New Event / BEO", icon: CalendarDays, module: "beo" },
];

export default function QuickAdd() {
  const { can, location, locations, switchLocation } = useAuth();
  const mobile = useIsMobile();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const { pathname } = useLocation();
  const hideFab = /^\/app\/inventory\/invoices\/[^/]+$/.test(pathname);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const on = (e) => setTyping(window.innerWidth < 1024 && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || ""));
    const off = () => setTyping(false);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);
  const auth = useRef(null);
  auth.current = { can, location, locations, switchLocation };
  useEffect(() => {
    const a = auth.current;
    const loc = sp.get("loc");
    if (loc && a.locations.some((l) => l.id === loc) && loc !== a.location?.id) a.switchLocation(loc);
    const q = sp.get("quick");
    if (q && ACTIONS.some((x) => x.key === q && !x.to && a.can(x.module))) setForm(q);
    if (q || loc) { sp.delete("quick"); sp.delete("loc"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);
  const pick = (a) => {
    setOpen(false);
    if (a.to) return nav(a.to);
    setTimeout(() => setForm(a.key), 150);
  };
  return (
    <>
      <button data-testid="quick-add-btn" onClick={() => setOpen(true)} aria-label="Quick Add"
        className={`no-print fixed z-40 right-3 bottom-[calc(env(safe-area-inset-bottom)+5rem)] lg:bottom-8 lg:right-8 h-12 w-12 sm:w-auto sm:h-14 justify-center sm:pl-4 sm:pr-5 rounded-full bg-[#9E2A2B] text-white shadow-xl flex items-center gap-2 font-semibold hover:bg-[#86201f] active:scale-95 transition-transform ${hideFab || typing ? "hidden" : ""}`}>
        <Plus className="h-5 w-5" /> <span className="hidden sm:inline">Quick Add</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side={mobile ? "bottom" : "right"} className={`${mobile ? "rounded-t-2xl" : ""} bg-white`} data-testid="quick-add-sheet">
          <SheetHeader className="text-left mb-2">
            <SheetTitle className="font-display text-2xl">Quick Add</SheetTitle>
            <SheetDescription className="text-xs text-stone-500">Logging to <span className="font-semibold text-stone-800">{location?.name}</span></SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {ACTIONS.filter((a) => can(a.module)).map((a) => (
              <button key={a.key} data-testid={`quick-add-${a.key}`} onClick={() => pick(a)}
                className="flex flex-col items-center justify-center gap-2 min-h-[92px] rounded-xl border border-[#E6E1D7] bg-[#FBF9F5] hover:bg-[#F3EFE6] active:scale-95 transition-transform p-2 text-center">
                <a.icon className="h-6 w-6 text-stone-800" />
                <span className="text-xs font-medium leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      {form && <FormSheet open={!!form} onOpenChange={(o) => !o && setForm(null)} formKey={form} onSaved={() => qc.invalidateQueries()} />}
    </>
  );
}
